import React, { useState } from 'react';
import { BadgeTooltip, BadgeTooltipContent, BadgeTooltipTrigger } from './BadgeTooltip';
import type { AchievementType } from '../MathEducationProvider';

export interface BadgeAnimationStartPayload {
  /** Unique achievement identifier for this badge instance. */
  badgeId: string;
  index: number;
  rect: DOMRect;
  type: AchievementType;
  icon: React.ReactNode;
  title?: string;
  description?: string;
}

export interface BadgeAnimationEndPayload {
  badgeId: string;
  index: number;
}

interface BadgeProps {
  /** Unique achievement identifier for this badge. */
  badgeId: string;
  type?: AchievementType;
  icon?: React.ReactNode;
  index: number;
  title?: string;
  description?: string;
  isSecret?: boolean;
  secretHint?: string;
  isSelected?: boolean;
  annotation?: string; // Extra info shown when selected
  onClick?: () => void;
  onAnimationStart?: (payload: BadgeAnimationStartPayload) => void;
  onAnimationEnd?: (payload: BadgeAnimationEndPayload) => void;
  shouldGlow?: boolean; // Adds green glow and pulse for recommended next goals
  isUnlocking?: boolean; // Badge is currently in the process of unlocking (used for display state)
  onUnlockComplete?: (badgeId: string) => void; // Called when unlock animation completes
}

interface BadgeStyles {
  bg: string;
  border: string;
  shadow: string;
  glow: string;
  // TODO(TEAME-183): Remove inline color fallbacks once Tailwind palette tokens are fully validated in host app.
  fallbackFaceStyle: React.CSSProperties;
}

export const Badge: React.FC<BadgeProps> = ({
  badgeId,
  type = 'empty',
  icon,
  index,
  title,
  description,
  isSecret = false,
  secretHint,
  isSelected = false,
  annotation,
  onClick,
  onAnimationStart,
  onAnimationEnd,
  shouldGlow = false,
  isUnlocking = false,
  onUnlockComplete,
}) => {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [shinePosition, setShinePosition] = useState<number>(0);
  const [rotation, setRotation] = useState<number>(0);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [showParticles, setShowParticles] = useState<boolean>(false);
  const badgeRef = React.useRef<HTMLDivElement>(null);
  const prevTypeRef = React.useRef<AchievementType>(type);
  const prevIsUnlockingRef = React.useRef<boolean>(isUnlocking);

  // Trigger unlock animation only when this badge transitions into the explicit unlocking state.
  // This prevents persisted unlocked badges from re-animating on hydration/reload.
  React.useEffect(() => {
    const unlockingStarted = !prevIsUnlockingRef.current && isUnlocking;
    prevIsUnlockingRef.current = isUnlocking;

    if (onAnimationStart || onAnimationEnd) {
      if (unlockingStarted && type !== 'empty') {
        setIsAnimating(true);
        if (badgeRef.current && onAnimationStart) {
          const rect = badgeRef.current.getBoundingClientRect();
          onAnimationStart({
            badgeId,
            index,
            rect,
            type,
            icon,
            title,
            description,
          });
        }
        const timer = setTimeout(() => {
          setIsAnimating(false);
          onAnimationEnd?.({ badgeId, index });
          // Call onUnlockComplete after animation finishes (event-driven cleanup)
          onUnlockComplete?.(badgeId);
        }, 600);
        prevTypeRef.current = type;
        return () => clearTimeout(timer);
      } else if (prevTypeRef.current !== type) {
        prevTypeRef.current = type;
      }
    } else if (prevTypeRef.current !== type) {
      // Original behavior: animate on any type change
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 600);
      prevTypeRef.current = type;
      return () => clearTimeout(timer);
    }
  }, [badgeId, type, isUnlocking, index, onAnimationStart, onAnimationEnd, onUnlockComplete]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width <= 0) return;
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const oppositeX = 100 - x;
    setShinePosition((oppositeX - 50) * 2);
    const rotationAngle = (50 - x) / 10; // -5deg..+5deg
    setRotation(rotationAngle);
  };

  // TODO(TEAME-183): Keep Tailwind utility colors and fallbackFaceStyle in sync; migrate to a single source of truth.
  const getBadgeStyles = (): BadgeStyles => {
    switch (type) {
      case 'gold':
        return {
          bg: 'tw-bg-gradient-to-br tw-from-yellow-300 tw-via-yellow-400 tw-to-yellow-600',
          border: 'tw-border-yellow-500',
          shadow: 'tw-shadow-xl tw-shadow-yellow-500/50',
          glow: '#fbbf24',
          fallbackFaceStyle: {
            backgroundColor: '#facc15',
            backgroundImage: 'linear-gradient(135deg, #fde047 0%, #facc15 52%, #ca8a04 100%)',
            borderColor: '#eab308',
            boxShadow: '0 10px 18px rgba(234, 179, 8, 0.45)',
          },
        };
      case 'silver':
        return {
          bg: 'tw-bg-gradient-to-br tw-from-gray-200 tw-via-gray-300 tw-to-gray-400',
          border: 'tw-border-gray-400',
          shadow: 'tw-shadow-xl tw-shadow-gray-400/50',
          glow: '#d1d5db',
          fallbackFaceStyle: {
            backgroundColor: '#d1d5db',
            backgroundImage: 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 52%, #9ca3af 100%)',
            borderColor: '#9ca3af',
            boxShadow: '0 10px 18px rgba(156, 163, 175, 0.42)',
          },
        };
      case 'bronze':
        return {
          bg: 'tw-bg-gradient-to-br tw-from-orange-400 tw-via-orange-500 tw-to-orange-700',
          border: 'tw-border-orange-600',
          shadow: 'tw-shadow-xl tw-shadow-orange-500/50',
          glow: '#f97316',
          fallbackFaceStyle: {
            backgroundColor: '#f97316',
            backgroundImage: 'linear-gradient(135deg, #fb923c 0%, #f97316 52%, #c2410c 100%)',
            borderColor: '#ea580c',
            boxShadow: '0 10px 18px rgba(249, 115, 22, 0.45)',
          },
        };
      default:
        return {
          bg: 'tw-bg-gray-200 dark:tw-bg-gray-700',
          border: 'tw-border-gray-400 dark:tw-border-gray-600',
          shadow: '',
          glow: 'transparent',
          fallbackFaceStyle: {
            backgroundColor: '#e5e7eb',
            borderColor: '#9ca3af',
            boxShadow: 'none',
          },
        };
    }
  };

  const styles = getBadgeStyles();
  const hasValue = type !== 'empty';
  const displayIcon = isSecret && !hasValue ? '❓' : icon || '🔒';

  const displayTitle = isSecret && !hasValue ? 'Secret Achievement' : title;
  const displayDescription =
    isSecret && !hasValue
      ? secretHint || 'Keep exploring and you might figure this one out for yourself!'
      : description;

  const handleClick = () => {
    if (onClick) {
      if (hasValue) {
        // Show particle splash for earned badges
        setShowParticles(true);
        setTimeout(() => setShowParticles(false), 800);
      }
      onClick();
    }
  };

  return (
    <BadgeTooltip>
      <BadgeTooltipTrigger asChild>
        <div
          ref={badgeRef}
          data-tooltip-anchor-padding="10"
          className="tw-relative tw-group tw-cursor-pointer"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => {
            setIsHovered(false);
            setRotation(0);
          }}
          onMouseMove={handleMouseMove}
          onClick={handleClick}
        >
          {/* Selection ring for all badges */}
          {isSelected && (
            <div
              className="tw-absolute tw-pointer-events-none"
              style={{
                inset: '-10px',
                borderRadius: '50%',
                boxShadow: '0 0 0 3px var(--primary), 0 0 12px 4px var(--primary)',
                animation: 'selectionPulse 2s ease-in-out infinite',
              }}
            />
          )}

          {/* Recommended next goal glow - green pulsing outline */}
          {shouldGlow && !isSelected && (
            <div
              className="tw-absolute tw-pointer-events-none"
              style={{
                inset: '-10px',
                borderRadius: '50%',
                boxShadow: '0 0 0 3px #22c55e, 0 0 16px 4px #22c55e',
                animation: 'recommendedPulse 2s ease-in-out infinite',
              }}
            />
          )}

          <div
            className="tw-relative"
            style={{
              width: '64px',
              height: '64px',
            }}
          >
            {/* Animation pulse effect */}
            {isAnimating && hasValue && (
              <>
                <div
                  className="tw-absolute tw-inset-0 tw-rounded-lg tw-animate-ping"
                  style={{
                    background: styles.glow,
                    opacity: 0.6,
                    clipPath: 'polygon(15% 0%, 85% 0%, 100% 15%, 100% 85%, 85% 100%, 15% 100%, 0% 85%, 0% 15%)',
                  }}
                />
                <style>{`
                  @keyframes badgePop {
                    0% { transform: scale(0.8) rotate(-10deg); opacity: 0; }
                    50% { transform: scale(1.15) rotate(5deg); }
                    100% { transform: scale(1) rotate(0deg); opacity: 1; }
                  }
                `}</style>
              </>
            )}

            <div
              className={`
                tw-w-16 tw-h-16 tw-border-2 tw-flex tw-items-center tw-justify-center
                tw-transform ${styles.bg} ${styles.border} ${styles.shadow}
                ${hasValue ? 'tw-scale-100 tw-opacity-100' : 'tw-scale-95 tw-opacity-60'}
              `}
              style={{
                clipPath: 'polygon(15% 0%, 85% 0%, 100% 15%, 100% 85%, 85% 100%, 15% 100%, 0% 85%, 0% 15%)',
                transform:
                  hasValue && isHovered
                    ? `scale(1.05) rotate(${rotation}deg)`
                    : !hasValue && isHovered
                    ? 'scale(1.05)'
                    : '',
                transition: isAnimating ? 'none' : 'transform 0.1s ease-out',
                animation: isAnimating ? 'badgePop 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)' : 'none',
                // TODO(TEAME-183): Remove inline overrides when Tailwind colors reliably match legacy visuals.
                ...styles.fallbackFaceStyle,
              }}
            >
              {/* Icon */}
              <div
                className={`tw-text-2xl ${hasValue ? '' : 'tw-opacity-30'}`}
                style={{
                  animation:
                    isAnimating && hasValue ? 'iconBounce 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)' : 'none',
                  // Strongly highlight icon on hover or when selected (remove gray opacity)
                  opacity: !hasValue && (isHovered || isSelected) ? 1 : undefined,
                  transition: 'opacity 0.2s ease-out',
                }}
              >
                {displayIcon}
              </div>

              {isAnimating && (
                <style>{`
                  @keyframes iconBounce {
                    0%, 100% { transform: scale(1); }
                    25% { transform: scale(0.8) rotate(-15deg); }
                    50% { transform: scale(1.3) rotate(15deg); }
                    75% { transform: scale(0.95) rotate(-5deg); }
                  }
                `}</style>
              )}

              {/* Shine effect - positioned opposite to mouse */}
              {hasValue && isHovered && (
                <div
                  className="tw-absolute tw-inset-0 tw-pointer-events-none tw-transition-transform tw-duration-100 tw-ease-out"
                  style={{
                    background: `linear-gradient(115deg, transparent 40%, rgba(255,255,255,0.7) 50%, transparent 60%)`,
                    clipPath: 'polygon(15% 0%, 85% 0%, 100% 15%, 100% 85%, 85% 100%, 15% 100%, 0% 85%, 0% 15%)',
                    transform: `translateX(${shinePosition}%)`,
                  }}
                />
              )}
            </div>

            {/* Glow effect */}
            {hasValue && (
              <div
                className="tw-absolute tw-inset-0 -tw-z-10 tw-blur-xl tw-opacity-50 tw-transition-opacity tw-duration-300"
                style={{
                  backgroundColor: styles.glow,
                  opacity: isHovered ? 0.7 : 0.4,
                }}
              />
            )}
          </div>
        </div>
      </BadgeTooltipTrigger>
      {displayTitle && (
        <BadgeTooltipContent side="top" align="center">
          <div className="tw-text-sm tw-font-atkinson tw-font-medium tw-mb-0.5">{displayTitle}</div>
          {displayDescription && (
            <div className="tw-text-xs tw-text-neutral-500 tw-font-atkinson">{displayDescription}</div>
          )}
          {annotation && isSelected && !hasValue && (
            <div className="tw-text-xs tw-mt-[var(--spacing-2)] tw-pt-[var(--spacing-2)] tw-border-t tw-border-neutral-100 tw-text-blue-500 tw-font-atkinson">
              {annotation}
            </div>
          )}
        </BadgeTooltipContent>
      )}

      {/* Animations */}
      {(showParticles || isSelected) && (
        <style>{`
          @keyframes selectionPulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          @keyframes recommendedPulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          @keyframes particle0 {
            0% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
            100% { transform: translate(-50%, -100%) scale(0.5); opacity: 0; }
          }
          @keyframes particle1 {
            0% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
            100% { transform: translate(20%, -100%) scale(0.5); opacity: 0; }
          }
          @keyframes particle2 {
            0% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
            100% { transform: translate(20%, -50%) scale(0.5); opacity: 0; }
          }
          @keyframes particle3 {
            0% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
            100% { transform: translate(20%, 0%) scale(0.5); opacity: 0; }
          }
          @keyframes particle4 {
            0% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
            100% { transform: translate(-50%, 0%) scale(0.5); opacity: 0; }
          }
          @keyframes particle5 {
            0% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
            100% { transform: translate(-120%, 0%) scale(0.5); opacity: 0; }
          }
          @keyframes particle6 {
            0% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
            100% { transform: translate(-120%, -50%) scale(0.5); opacity: 0; }
          }
          @keyframes particle7 {
            0% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
            100% { transform: translate(-120%, -100%) scale(0.5); opacity: 0; }
          }
        `}</style>
      )}
    </BadgeTooltip>
  );
};
